import {
	EvCard,
	EvCheckbox,
	EvIcon,
	EvLink,
	EvTextInput,
	EvTitle
} from '@evinced-private/ui-common';
import classnames from 'classnames';
import React, { FC, useEffect, useMemo, useState } from 'react';
import EvincedLogo from '../../resources/images/logo_evinced.svg';
import './TermsConsentPage.scss';

const titleText = 'Welcome aboard!';
const descriptionText = 'We just need a few more details to get started.';
const consentLineText = 'I accept the terms of the';
const eulaLinkText = 'End User License Agreement';
const privacyPolicyLinkText = 'Privacy Policy';
const submitText = "Let's Go";

const isStrEmpty = (value: string): boolean => {
	return value === '' || !value;
};

const TermsConsentPage: FC = () => {
	const query = new URLSearchParams(window.location.search);
	const state = query.get('state');
	const givenName = query.get('first_name');
	const familyName = query.get('last_name');
	const isSamlProvider = query.get('is_saml_provider');
	const auth0Domain = query.get('auth0_domain');

	const EULA_LINK = 'https://www.evinced.com/eula';
	const PRIVACY_POLICY_LINK = 'https://www.evinced.com/privacy-policy';

	const [firstName, setFirstName] = useState<string>(givenName);
	const [lastName, setLastName] = useState<string>(familyName);
	const [consentGiven, setConsentGiven] = useState(false);
	const [submitEnabled, setSubmitEnabled] = useState(false);
	const continueUrl = `https://${auth0Domain}/continue`;

	useEffect(() => {
		setSubmitEnabled(!isStrEmpty(firstName) && !isStrEmpty(lastName) && consentGiven);
	}, [firstName, lastName, consentGiven]);

	const onFirstNameChange = (e): void => {
		setFirstName(e.target.value);
	};

	const onLastNameChange = (e): void => {
		setLastName(e.target.value);
	};

	const renderSubmitForm = useMemo((): JSX.Element => {
		return (
			<form
				action={`${continueUrl}?state=${state}&termsApproved=${consentGiven}&firstName=${firstName}&lastName=${lastName}`}
				method="post"
			>
				<div className="main-text">
					<div className="personal-details">
						<div className="name-field">
							<div id="first-name-label" className="label">First name</div>
							<EvTextInput
								className="first-name-input"
								ariaLabelledBy="first-name-label"
								value={firstName}
								onChange={onFirstNameChange}
								isDisabled={isSamlProvider === 'true'}
								isNew
							/>
						</div>
						<div className="name-field">
							<div id="last-name-label" className="label">Last name</div>
							<EvTextInput
								className="last-name-input"
								ariaLabelledBy="last-name-label"
								value={lastName}
								onChange={onLastNameChange}
								isDisabled={isSamlProvider === 'true'}
								isNew
							/>
						</div>
					</div>
					<div className="terms-and-conditions" id="termsApproved">
						<EvCheckbox
							isChecked={consentGiven}
							onClick={() => setConsentGiven((state) => !state)}
							ariaLabel={`${consentLineText} ${eulaLinkText} and the ${privacyPolicyLinkText}`}
							isNew
						/>
						<div className="eula-text">
							<p aria-hidden={true}>
								{consentLineText}
								<EvLink url={EULA_LINK} openInNewTab={true} linkText={eulaLinkText} isNew />
								&nbsp;and the
								<EvLink
									url={PRIVACY_POLICY_LINK}
									openInNewTab={true}
									linkText={privacyPolicyLinkText}
									isNew
								/>
								.
							</p>
						</div>
					</div>
					<input
						className={classnames('submit-btn', { active: submitEnabled })}
						type="submit"
						value={submitText}
						disabled={!submitEnabled}
					/>
				</div>
			</form>
		);
	}, [consentGiven, continueUrl, firstName, isSamlProvider, lastName, state, submitEnabled]);

	return (
		<main className="terms-consent">
			<div className="card-and-logo-container">
				<EvCard className="terms-consent-card">
					<>
						<EvIcon icon={EvincedLogo} className="evinced-logo" />
						<EvTitle titleText={titleText} isNew className="terms-consent-title" />
						<p className="terms-consent-description">{descriptionText}</p>
						{renderSubmitForm}
					</>
				</EvCard>
			</div>
		</main>
	);
};

export default TermsConsentPage;
